import { Show } from 'solid-js'
import type { MeasurementEntry, MetricDefinition } from '@fasting-timer/shared'

import { useI18n } from '../../i18n'
import { describeEntry, formatDistance, formatMetricValue } from './measurementFormat'
import { getPrimaryValue } from './metricViewUtils'

type MetricSummaryCardProps = {
   definition: MetricDefinition
   best: MeasurementEntry | null
   latest: MeasurementEntry | null
   group: number | null
}

/**
 * Renders the headline card: best primary value for the active group plus the
 * latest entry.
 *
 * @param props - The metric, best/latest entries, and active group.
 * @returns The card element.
 */
const MetricSummaryCard = (props: MetricSummaryCardProps) => {
   const t = useI18n()

   /**
    * Formats the primary value of an entry.
    *
    * @param entry - The entry.
    * @returns The formatted value, or a placeholder.
    */
   const primary = (entry: MeasurementEntry) => {
      const value = getPrimaryValue(entry, props.definition)
      return value === null ? '--' : formatMetricValue(props.definition.primary, value)
   }

   return (
      <section
         id="measurement-summary"
         class="bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-3 shadow-lg shadow-emerald-900/40 flex flex-col gap-1"
      >
         <p class="text-xs uppercase tracking-wider text-emerald-200">
            <Show
               when={props.group !== null}
               fallback={t('Best {value}', { value: t(props.definition.primary) })}
            >
               {t('Best {distance} {value}', {
                  distance: formatDistance(props.group!, 1),
                  value: t(props.definition.primary),
               })}
            </Show>
         </p>
         <Show
            when={props.best}
            fallback={
               <p class="text-sm text-emerald-100/80">
                  {t('No {metric} entries yet.', {
                     metric: t(props.definition.labelKey),
                  })}
               </p>
            }
         >
            {best => (
               <>
                  <p class="text-3xl font-black text-emerald-200 leading-tight">
                     {primary(best())}
                  </p>
                  <p class="text-sm text-emerald-100/80">
                     {describeEntry(best(), props.definition).join(' · ')}
                  </p>
               </>
            )}
         </Show>
         <Show when={props.latest !== props.best ? props.latest : null}>
            {latest => (
               <p class="text-xs text-emerald-100/60">
                  {t('Latest')}: {describeEntry(latest(), props.definition).join(' · ')}
               </p>
            )}
         </Show>
      </section>
   )
}

export default MetricSummaryCard
